<script setup lang="ts">
import { Button, Icon } from "@/components/ui"
import { useTranslation } from "@/composables"

interface GuestEventFloatingActionsProps {
  visible: boolean
  albumsShortcutVisible?: boolean
  shareLinkVisible?: boolean
}

const props = withDefaults(defineProps<GuestEventFloatingActionsProps>(), {
  albumsShortcutVisible: false,
  shareLinkVisible: false,
})

const emit = defineEmits<{
  (e: "shareLinkClick"): void
}>()

const { t } = useTranslation()

const albumsSticky = defineModel<boolean>("albumsSticky", { default: false })

function scrollToTop() {
  window.scrollTo({ top: 0, behavior: "smooth" })
}

function onAlbumsShortcutClick() {
  albumsSticky.value = !albumsSticky.value
}

function onShareLinkClick() {
  emit("shareLinkClick")
}
</script>

<template>
  <Transition name="gpx-fade">
    <div
      v-show="visible"
      class="fixed bottom-2 right-6 z-30 flex flex-col items-end gap-2 md:bottom-4 md:flex-row md:items-end"
      data-test="guest-event-floating-actions-wrap"
    >
      <div class="flex items-center gap-2">
        <!-- album button -->
        <Button
          v-if="props.albumsShortcutVisible"
          variant="secondary"
          shape="pill"
          class="h-12 w-12 min-w-12 shrink-0 flex-col justify-center py-1 shadow-lg backdrop-blur-sm transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 focus-visible:ring-offset-2 md:h-12 md:min-h-0 md:w-auto md:flex-row md:px-4 md:py-2 [&_svg]:block"
          data-test="guest-event-albums-shortcut"
          @click="onAlbumsShortcutClick"
        >
          <Icon
            :icon="albumsSticky ? 'Close' : 'Folder'"
            size="sm"
            class="max-md:size-4"
          />
          <span class="text-[9px] leading-none font-medium whitespace-nowrap md:text-sm -mt-1 md:mt-0">
            {{ t("gallery_floating_actions.albums_shortcut") }}
          </span>
        </Button>
        <!-- share link button -->
        <Button
          v-if="props.shareLinkVisible"
          variant="secondary"
          shape="pill"
          class="h-12 w-12 min-w-12 shrink-0 flex-col justify-center py-1 shadow-lg backdrop-blur-sm transition-colors duration-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 focus-visible:ring-offset-2 md:h-12 md:min-h-0 md:w-auto md:flex-row md:px-4 md:py-2 [&_svg]:block"
          data-test="guest-event-share-link-shortcut"
          @click="onShareLinkClick"
        >
          <Icon icon="Link" size="sm" class="max-md:size-4" />
          <span class="text-[9px] md:text-sm leading-none font-medium whitespace-nowrap -mt-1 md:mt-0">
            {{ t("nav.share_short") }}
          </span>
        </Button>
        <!-- scroll to top button -->
        <Button
          variant="primary"
          shape="circle"
          class="shadow-lg focus:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 focus-visible:ring-offset-2"
          data-test="guest-event-scroll-to-top"
          @click="scrollToTop"
        >
          <Icon icon="ChevronsUp" size="xs" />
        </Button>
      </div>
    </div>
  </Transition>
</template>
